import minBy from 'lodash/minBy';
import type { ILine } from '../models/line.model';
import Colors from './colors.helper';
import { VasttrafikGidHelper } from './vasttrafik-gid.helper';
import { PublicTransportAuthorityCodes } from './public-transport-authority-codes';
import { TransportMode } from '../models/transport-mode.enum';

export class LineHelper {
  // NV-8215: Suffixes that may appear in line.name and should be separated from the base name.
  // Keep in sync with LineNameSuffixesToStrip in XhtmlStringHelper.cs.
  private static readonly lineNameReplacementTrafficSuffixes: ReadonlyArray<string> = ['ersätter västtåg', 'ersätter öresundståg', 'ersätter vy', 'ersätter sj regional'];

  // NV-8215: Extracts a known suffix from line.name, returning it with its original casing.
  // Returns undefined if line.name contains no recognised suffix.
  // Example: line.name = "22 Ersätter västtåg" → "Ersätter västtåg"
  public static extractLineNameReplacementTrafficSuffix(line: ILine): string | undefined {
    const lowerName = line.name?.toLowerCase() ?? '';
    const suffix = LineHelper.lineNameReplacementTrafficSuffixes.find((s) => lowerName.includes(s));
    if (!suffix) return undefined;
    const index = lowerName.indexOf(suffix);
    return line.name.substring(index, index + suffix.length);
  }

  public static getLineNameAndDirection(line: ILine): string {
    if (!line) {
      return '';
    }
    return `${line.name ? line.name : line.shortName} ${line.directionName}`;
  }

  public static areLinesPracticallyTheSame(line1: ILine, line2: ILine): boolean {
    return line1.gid === line2.gid && line1.directionName === line2.directionName && line1.foregroundColor === line2.foregroundColor && line1.backgroundColor === line2.backgroundColor;
  }

  public static getAppliesFromText(line: ILine, translationFrom: string) {
    return `${translationFrom.toLocaleLowerCase()} ${line.appliesFrom?.toFormat('yyyy-MM-dd') ?? ''}`;
  }

  // Read NV-2749 for more background on this.
  // Lines are versioned by the composite key of gid + number, and they have a validity date interval.
  // For travel planning we display future line versions that are not yet valid, such that users can plan their trip ahead of time.
  // Sometimes only trivial fields, like contractorId, change from one version to another.
  // For end users, these are practically duplicates, so we only display the currently valid one.
  private static getRelevantLine?(lines: ILine[], line: ILine): ILine | undefined {
    const practicallyTheSameLines = lines.filter((l) => LineHelper.areLinesPracticallyTheSame(l, line));

    if (practicallyTheSameLines.length === 0) {
      return undefined;
    }

    if (practicallyTheSameLines.length === 1) {
      return line;
    }

    const earliestLine = minBy(practicallyTheSameLines, 'appliesFrom');
    if (earliestLine === undefined) {
      return undefined;
    }

    const earliestLineAppliesFrom = earliestLine.appliesFrom;

    if (line.appliesFrom === earliestLineAppliesFrom) {
      return line;
    }

    return undefined;
  }

  public static getRelevantLines(lines: ILine[]): ILine[] {
    const distinctLines: ILine[] = [];

    lines.forEach((line) => {
      const distinctLine = LineHelper.getRelevantLine ? LineHelper.getRelevantLine(lines, line) : undefined;

      if (distinctLine) {
        distinctLines.push(distinctLine);
      }
    });

    return distinctLines;
  }

  public static getBackgroundColor(line: { backgroundColor: string | undefined } | undefined): string {
    if (line && line.backgroundColor) {
      if (line.backgroundColor[0] === '#') {
        return line.backgroundColor;
      }
      return `#${line.backgroundColor}`;
    }
    return '#006C93';
  }

  public static getForegroundColor(line: { foregroundColor: string | undefined } | undefined): string {
    if (line && line.foregroundColor) {
      if (line.foregroundColor[0] === '#') {
        return line.foregroundColor;
      }
      return `#${line.foregroundColor}`;
    }
    return '#FFFFFF';
  }

  public static getBorderColor(line: { foregroundColor: string | undefined; backgroundColor: string | undefined; borderColor: string | undefined } | undefined): string {
    if (!line || !line.borderColor) {
      return Colors.vtdGray200;
    }
    const normalizedBorderColor = LineHelper.normalizeColor(line.borderColor);

    // NV-8449: Lines with these APR border colors don't have enough contrast in some UI contexts
    // (white disappears against white backgrounds; light blue disappears against the journey-search
    // background), so we fall back to the foreground color for blue (#d0edfb) and background color
    // for white (#ffffff) instead.
    if (normalizedBorderColor === '#d0edfb') {
      return LineHelper.getForegroundColor(line);
    }
    if (normalizedBorderColor === '#ffffff') {
      return LineHelper.getBackgroundColor(line);
    }
    return normalizedBorderColor;
  }

  private static normalizeColor(color: string): string {
    const withHash = color[0] === '#' ? color : `#${color}`;
    return withHash.toLowerCase();
  }

  public static getBackgroundSafeForegroundColor(line: ILine | undefined): string | undefined {
    const foregroundColor = line?.foregroundColor;
    if (LineHelper.isBackgroundSafeForegroundColor(foregroundColor, line)) {
      return foregroundColor;
    }
    return line?.backgroundColor;
  }

  public static getBackgroundSafeBackgroundColor(line: ILine | undefined): string | undefined {
    const foregroundColor = line?.foregroundColor;
    if (LineHelper.isBackgroundSafeForegroundColor(foregroundColor, line)) {
      return line?.backgroundColor;
    }
    return foregroundColor;
  }

  private static isBackgroundSafeForegroundColor(foregroundColor: string | undefined, line: ILine | undefined): boolean {
    if (!foregroundColor || !line) return true;
    return foregroundColor !== '#ffffff' && foregroundColor !== '#ffff50';
  }

  public static getLineName(line: ILine, useShortName: boolean = false, useShortNameAndNumberForTrain: boolean = false): string {
    // For train lines, return name + designation (if designation exists) or just name
    if (line.transportMode === TransportMode.train) {
      const designation = line.designation?.toLocaleLowerCase() !== 'tåg' ? line.designation : undefined;

      if (useShortNameAndNumberForTrain) {
        return designation ? `${LineHelper.getTrainName(line, true)} ${designation}` : LineHelper.getTrainName(line, true);
      }

      return useShortName ? line.shortName : designation ? `${LineHelper.getTrainName(line, false)} ${designation}` : LineHelper.getTrainName(line, false);
    }

    // For all other transport modes, return shortName
    return line.shortName;
  }

  private static getTrainName(line: ILine, useTrainShortName: boolean = false): string {
    // Apr will not send transportAuthorityGid, but will provide the line name directly
    if (!line.transportAuthorityGid) {
      return !useTrainShortName
        ? line.name
        : (() => {
            switch (line.name) {
              case 'Västtågen':
                return 'V-TÅG';
              case 'Vy':
                return 'VY';
              case 'Öresundståg':
                return 'Ö-TÅG';
              case 'SJ Regional':
                return 'SJ';
              case 'TÅGAB Regiontåg':
                return 'TÅGAB';
              default:
                return line.name;
            }
          })();
    }

    const publicTransportAuthority = VasttrafikGidHelper.getPublicTransportAuthority(line.transportAuthorityGid);

    // NV-8873: For lines from LineRegistry with transportAuthorityGid, resolve train name from the transport authority GID.
    switch (publicTransportAuthority) {
      case PublicTransportAuthorityCodes.Vasttrafik:
        return useTrainShortName ? 'V-TÅG' : 'Västtågen';
      case PublicTransportAuthorityCodes.Oresundstag:
        return useTrainShortName ? 'Ö-TÅG' : 'Öresundståg';
      case PublicTransportAuthorityCodes.Vy:
        return useTrainShortName ? 'VY' : 'Vy';
      case PublicTransportAuthorityCodes.Sj:
        return useTrainShortName ? 'SJ' : 'SJ Regional';
      default:
        return useTrainShortName ? line.shortName : line.name;
    }
  }
}
